import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { dmsTagTypes, IrisDMSTag, IrisDMSTagI, IrisDMSTagType } from '@iris/common/modules/dms/models/IrisDMSTag';
import { IrisDmsPermissionsSandbox } from '@iris/common/modules/dms/sandbox/dms-permissions.sandbox';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { switchMap, take, tap, of, catchError, EMPTY } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrisDmsTagsStore } from '../../../store/tags.signal-store';
import { IrisQueryParamsBuilder } from '@iris/api-query';
import { IrisConfirmationService } from '@iris/common/services/confirmation.service';
import { IrisTagsService } from '@iris/common/services/tags/tags.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { IrisFieldsModule } from '@iris/common/modules/fields/fields.module';
import {
  StrakitButtonComponent,
  StrakitSelectComponent,
  StrakitTextComponent,
  StrakitToggleComponent,
} from '@ngx-strakit/cdk/controls';
import { StrakitColorComponent } from '@ngx-strakit/cdk/controls/color';
import { AsyncPipe } from '@angular/common';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

@Component({
  selector: 'iris-dms-tag-modal',
  imports: [
    ReactiveFormsModule,

    MatFormFieldModule,
    TranslatePipe,
    AsyncPipe,

    IrisFieldsModule,

    SkModalContentComponent,
    StrakitTextComponent,
    StrakitSelectComponent,
    StrakitToggleComponent,
    StrakitColorComponent,

    ConfirmByKeypressDirective,
    StrakitButtonPrefixDirective,
    StrakitButtonComponent,
  ],
  templateUrl: './dms-tag-modal.component.html',
  styleUrls: ['./dms-tag-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsTagModalComponent extends SkModalBase<IrisDmsTagModalComponent, IrisDMSTagI> implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly tagsService = inject(IrisTagsService);
  private readonly confirmationService = inject(IrisConfirmationService);
  private readonly translate = inject(TranslateService);
  readonly data = inject<{
    tag: IrisDMSTagI;
  }>(MAT_DIALOG_DATA);

  private readonly tagsStore = inject(IrisDmsTagsStore);

  @ViewChild(StrakitColorComponent, { static: true }) fieldColor: StrakitColorComponent;

  readonly tagTypes = dmsTagTypes.map(tagType => ({
    name: tagType.name,
    label: this.translate.instant(tagType.label),
  }));

  private readonly projectId = toSignal(this.globalSandbox.currentProjectId$);

  public tagForm: FormGroup = this.fb.group({
    name: new FormControl<string>('', [Validators.required]),
    type: new FormControl<IrisDMSTagType>(null),
    color: new FormControl<string>(''),
    isActive: new FormControl<boolean>(true),
  });

  private _tag: IrisDMSTagI = new IrisDMSTag();

  set tag(tag: IrisDMSTagI) {
    if (tag) {
      this._tag = new IrisDMSTag(tag);
      this.tagForm.patchValue({
        name: this.tag.name,
        type: this.getTagType(tag),
        color: this.tag.color,
        isActive: this.tag.isActive,
      });
    }
  }

  get tag(): IrisDMSTagI {
    return this._tag;
  }

  get label(): string {
    return !this.tag.id ? 'label.AddNewTag' : 'label.EditTag';
  }

  get isAdmin(): boolean {
    return this.dmsPermissionsSandbox.isAdmin;
  }

  ngOnInit(): void {
    this.setTagType();
    this.tag = this.data.tag ?? null;
  }

  saveTag(): void {
    const { name, color, type, isActive } = this.tagForm.getRawValue() as { name: string; color: string; type: IrisDMSTagType; isActive: boolean };

    const needToCheckDuplicate = name !== this.tag.name || type !== this.getTagType(this.tag);

    const projectId = type === IrisDMSTagType.Project ? this.projectId() : null;
    const tag = new IrisDMSTag({ ...this.tag, name, value: name, color, isActive, projectId });

    if (!needToCheckDuplicate) {
      this.processTagSaving(tag);
      return;
    }

    this.tagsStore.checkTagExistence$(tag.name, tag.projectId).pipe(
      take(1),
      tap(exists => this.processTagSaving(tag, exists)),
    ).subscribe();
  }

  private setTagType(): void {
    const tagTypeControl = this.tagForm.get('type');

    if (!this.isAdmin) {
      tagTypeControl.setValue(IrisDMSTagType.Project);
    }

    if (!this.projectId() && tagTypeControl.value !== IrisDMSTagType.Public) {
      tagTypeControl.setValue(IrisDMSTagType.Public);
    }

    this.updateTypeDisableStatus();
  }

  private updateTypeDisableStatus(): void {
    if (!this.projectId() || !this.isAdmin) {
      this.tagForm.get('type').disable();
    } else {
      this.tagForm.get('type').enable();
    }
  }

  private getTagType(tag: IrisDMSTagI): IrisDMSTagType {
    return tag.projectId ? IrisDMSTagType.Project : IrisDMSTagType.Public;
  }

  private processTagSaving(tag: IrisDMSTagI, isDuplicate = false): void {
    if (isDuplicate) {
      this.tagForm.setErrors({ tagExists: true });
      this.changeDetector.markForCheck();
      return;
    }

    const params = new IrisQueryParamsBuilder()
      .urlParam('tagName', tag.name)
      .urlParam('tagType', 'DMS')
      .toStructure();

    this.tagsService.getProjectsWithTheSameTag(params).pipe(
      take(1),
      switchMap(projects => {
        if (projects.length && this.getTagType(tag) === IrisDMSTagType.Public) {
          const projectsString = projects.map(project => `"${project.name}"`).join(', ');

          return this.confirmationService.show$({
            message: this.translate.instant('label.dms.ProjectTagWithTheSameNameIsExist', { projects: projectsString }),
            doNotEmitOnDismiss: true,
          }).pipe(
            take(1),
            tap(() => {
              this.finishTagSaving(tag);
              this.closeWithResult(tag);
            }),
          );
        } else {
          this.finishTagSaving(tag);
          this.closeWithoutResult();
          return of(null);
        }
      }),
      catchError(() => EMPTY),
    ).subscribe();
  }

  private finishTagSaving(tag: IrisDMSTagI): void {
    if (tag.id) {
      this.tagsStore.updateTag(tag);
    } else {
      this.tagsStore.createTag(tag);
    }
  }
}
